import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  PlusCircle,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import UserList from '@/components/users/user-list';
import { UserFilters } from '@/components/users/user-filters';
import { cn } from '@/lib/utils';
import { useUsersQuery } from '@/hooks/queries/useUsers';
import { useCanManageUsers } from '@/hooks/usePermissions';
import type { ApiUser, User } from '@/types/api';

// Helper function to convert API user to Store user format for UI compatibility
const apiUserToStoreUser = (apiUser: ApiUser): User => ({
  id: apiUser.id,
  name: apiUser.name,
  email: apiUser.email,
  role: mapApiRoleToStoreRole(apiUser.roles[0] || 'viewer'),
  status: apiUser.is_active ? 'Active' : 'Inactive',
  propertyId: apiUser.property_id || null,
  tenantId: null,
  avatar: '', // API doesn't provide avatar, will use initials
  fallback: apiUser.name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase(),
  permissions: [], // Will be populated from user detail if needed
  createdAt: apiUser.created_at || new Date().toISOString(),
  updatedAt: apiUser.updated_at || new Date().toISOString(),
});

// Helper function to map API roles to store role format
const mapApiRoleToStoreRole = (apiRole: string): User['role'] => {
  switch (apiRole.toLowerCase()) {
    case 'admin':
      return 'admin';
    case 'manager':
      return 'Manager';
    case 'auditor':
      return 'Auditor';
    case 'department_head':
      return 'Department Head';
    case 'viewer':
      return 'Viewer';
    default:
      return 'Viewer';
  }
};

export default function UsersPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);
  const canManageUsers = useCanManageUsers();

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedProperty, setSelectedProperty] = useState('all');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Build query parameters for API
  const queryParams = useMemo(
    () => ({
      page: currentPage,
      page_size: itemsPerPage,
      ...(searchTerm && { search: searchTerm }),
      ...(selectedRole !== 'all' && { role: selectedRole }),
      ...(selectedProperty !== 'all' && { property: selectedProperty }),
    }),
    [currentPage, searchTerm, selectedRole, selectedProperty]
  );

  // Use regular paginated query for users
  const { data: usersResponse, isLoading, error } = useUsersQuery(queryParams);

  // Convert API users to store format for UI compatibility
  const displayUsers = useMemo(() => {
    if (!usersResponse?.data) return [];
    return usersResponse.data.map(apiUserToStoreUser);
  }, [usersResponse]);

  // Calculate pagination info
  const totalCount = usersResponse?.pagination?.count || 0;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedRole, selectedProperty]);

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleNewUser = () => {
    navigate('/users/create-user');
  };

  return (
    <div className="flex flex-col gap-4 sm:gap-8 p-3 sm:p-6">
      {/* Header */}
      <div
        className={cn(
          'flex flex-col sm:flex-row sm:items-center justify-between gap-3',
          isRTL ? 'sm:flex-row-reverse' : 'sm:flex-row'
        )}
      >
        <div className={cn('space-y-1', isRTL ? 'text-right' : 'text-left')}>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold font-headline text-primary">
            {t('users.management.title')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('users.management.description')}
          </p>
        </div>
        {canManageUsers && (
          <Button onClick={handleNewUser} className="self-start sm:self-auto">
            <PlusCircle className={cn('h-4 w-4', isRTL ? 'ml-2' : 'mr-2')} />
            <span className="hidden sm:inline">
              {t('users.management.addNewUser')}
            </span>
            <span className="sm:hidden">{t('users.management.addUser')}</span>
          </Button>
        )}
        </div>

        {/* Content */}
        <Card className="bg-card/50 backdrop-blur-sm border-border/50">
          <CardHeader className="p-3 sm:p-6">
            <CardTitle
              className={cn(
                'font-headline text-primary text-base sm:text-lg',
                isRTL ? 'text-right' : 'text-left'
              )}
            >
              {t('users.management.allUsers')}
            </CardTitle>
            <CardDescription
              className={cn('text-sm', isRTL ? 'text-right' : 'text-left')}
            >
              {t('users.management.overview')}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0">
            {/* User Filters */}
            <UserFilters
              searchTerm={searchTerm}
              selectedRole={selectedRole}
              selectedProperty={selectedProperty}
              onSearchChange={setSearchTerm}
              onRoleChange={setSelectedRole}
              onPropertyChange={setSelectedProperty}
              onReset={() => {
                setSearchTerm('');
                setSelectedRole('all');
                setSelectedProperty('all');
              }}
            />

            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-4">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">
                    {t('users.management.loading', 'Loading users...')}
                  </p>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-4 text-center">
                  <AlertCircle className="h-8 w-8 text-destructive" />
                  <div>
                    <p className="font-medium text-destructive">
                      {t('users.management.error', 'Failed to load users')}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {error.message}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* User List */}
            {!isLoading && !error && (
              <>
                <UserList users={displayUsers} />

                {/* Pagination Controls - Fully Responsive */}
                {displayUsers.length > 0 && (
                  <Card className="mt-4">
                    <CardContent className="py-3 sm:py-4 px-3 sm:px-4">
                      <div className="flex items-center justify-center">
                        <div
                          className={cn(
                            'flex items-center justify-center flex-wrap gap-2 sm:gap-3 w-full',
                            isRTL ? 'flex-row-reverse' : 'flex-row'
                          )}
                        >
                          {/* Previous Button */}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="h-10 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm min-w-fit"
                          >
                            {isRTL ? (
                              <>
                                <span className="hidden sm:inline">
                                  {t('pagination.previous')}
                                </span>
                                <span className="sm:hidden">Prev</span>
                                <ChevronRight className="w-4 h-4 ml-1" />
                              </>
                            ) : (
                              <>
                                <ChevronLeft className="w-4 h-4 mr-1" />
                                <span className="hidden sm:inline">
                                  {t('pagination.previous')}
                                </span>
                                <span className="sm:hidden">Prev</span>
                              </>
                            )}
                          </Button>

                          {/* Page numbers - Show on sm and up */}
                          <div className="hidden sm:flex items-center gap-1">
                            {Array.from(
                              { length: Math.min(5, totalPages) },
                              (_, i) => {
                                let pageNum;
                                if (totalPages <= 5) {
                                  pageNum = i + 1;
                                } else if (currentPage <= 3) {
                                  pageNum = i + 1;
                                } else if (currentPage >= totalPages - 2) {
                                  pageNum = totalPages - 4 + i;
                                } else {
                                  pageNum = currentPage - 2 + i;
                                }

                                return (
                                  <Button
                                    key={pageNum}
                                    variant={
                                      currentPage === pageNum
                                        ? 'default'
                                        : 'outline'
                                    }
                                    size="sm"
                                    onClick={() => handlePageChange(pageNum)}
                                    className="w-9 h-9 p-0 text-xs sm:text-sm"
                                  >
                                    {pageNum}
                                  </Button>
                                );
                              }
                            )}
                          </div>

                          {/* Page info for mobile - Responsive text sizing */}
                          <div className="sm:hidden text-xs sm:text-sm text-muted-foreground px-2 py-1 bg-muted/50 rounded min-w-[60px] text-center">
                            {currentPage}/{totalPages}
                          </div>

                          {/* Next Button */}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="h-10 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm min-w-fit"
                          >
                            {isRTL ? (
                              <>
                                <ChevronLeft className="w-4 h-4 mr-1" />
                                <span className="hidden sm:inline">
                                  {t('pagination.next')}
                                </span>
                                <span className="sm:hidden">Next</span>
                              </>
                            ) : (
                              <>
                                <span className="hidden sm:inline">
                                  {t('pagination.next')}
                                </span>
                                <span className="sm:hidden">Next</span>
                                <ChevronRight className="w-4 h-4 ml-1" />
                              </>
                            )}
                          </Button>
                        </div>
                      </div>

                      {/* Results info */}
                      <div className="mt-3 text-center">
                        <p className="text-xs text-muted-foreground">
                          {t('users.management.showingResults', {
                            start: (currentPage - 1) * itemsPerPage + 1,
                            end: Math.min(
                              currentPage * itemsPerPage,
                              totalCount
                            ),
                            total: totalCount,
                          })}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* No Users Found */}
                {displayUsers.length === 0 && !isLoading && (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">
                      {searchTerm ||
                      selectedRole !== 'all' ||
                      selectedProperty !== 'all'
                        ? t(
                            'users.management.noUsersFound',
                            'No users found matching your filters'
                          )
                        : t('users.management.noUsers', 'No users available')}
                    </p>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
  );
}
